import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  IconButton,
  Modal,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  Divider,
} from "@mui/material";
import React from "react";
import { PersonOutline } from "@mui/icons-material";

export type ViewDetailsItem = {
  primary: string;
  secondary?: string;
  rightText?: string;
  /** Left indentation in px (e.g. for subgroup hierarchy) */
  indent?: number;
  /** Renders primary text as an italic, muted placeholder (e.g. "No version linked yet") */
  emptyState?: boolean;
};

interface ViewDetailsModalProps {
  openViewDetailsModal: boolean;
  setOpenViewDetailsModal: React.Dispatch<React.SetStateAction<boolean>>;
  title: string;
  items: ViewDetailsItem[];
  emptyMessage?: string;
  icon?: React.ReactNode;
}

export const ViewDetailsModal: React.FC<ViewDetailsModalProps> = ({
  openViewDetailsModal,
  setOpenViewDetailsModal,
  title,
  items,
  emptyMessage = "No items selected",
  icon,
}) => {
  const defaultIcon = icon || <PersonOutline sx={{ height: "24px" }} />;

  return (
    <Modal
      open={openViewDetailsModal}
      onClose={() => setOpenViewDetailsModal(false)}
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          maxHeight: "60vh",
          overflowY: "auto",
          backgroundColor: "white",
          borderRadius: "8px",
          boxShadow: "0px 3px 6px rgba(0, 0, 0, 0.24)",
          padding: "16px",
          width: "50%",
          maxWidth: "413px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Typography
            variant="h5"
            sx={{
              flex: 1,
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {title}
          </Typography>

          <IconButton
            sx={{
              border: "none",
              flexShrink: 0,
            }}
            onClick={() => setOpenViewDetailsModal(false)}
          >
            <CloseIcon sx={{ color: "#98A2B3" }} />
          </IconButton>
        </Box>
        <Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <Divider sx={{ my: 1, width: "100%" }} />
            <List disablePadding sx={{ p: "0", width: "100%", pt: "10px" }}>
              {items.length === 0 ? (
                <Typography>{emptyMessage}</Typography>
              ) : (
                items.map((item, index) => {
                  return (
                    <ListItem
                      key={index}
                      disablePadding
                      sx={{
                        mb: "13px",
                        alignItems: "center",
                        pl: item.indent ?? 0,
                      }}
                    >
                      <ListItemAvatar sx={{ minWidth: "auto", pr: "12px" }}>
                        <Avatar
                          sx={{
                            backgroundColor: "#F2F4F7",
                            padding: "6px",
                            width: "32px",
                            height: "32px",
                            color: item.emptyState ? "#98A2B3" : undefined,
                          }}
                        >
                          {defaultIcon}
                        </Avatar>
                      </ListItemAvatar>

                      <ListItemText
                        primary={item.primary}
                        secondary={item.secondary}
                        slotProps={{
                          primary: item.emptyState
                            ? { sx: { fontStyle: "italic", color: "#98A2B3" } }
                            : undefined,
                        }}
                        sx={{ m: 0, flex: 1 }}
                      />
                      {item.rightText && (
                        <Typography
                          sx={{
                            color: "text.secondary",
                            fontSize: "0.875rem",
                            ml: 2,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {item.rightText}
                        </Typography>
                      )}
                    </ListItem>
                  );
                })
              )}
            </List>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};
